import type { ApiErrorBody } from '@nw/shared';

/** Error thrown by `api()` — `code` comes from the server error shape. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

type Json = Record<string, unknown> | unknown[] | null;

/**
 * Fetch wrapper for the admin API (same origin, session cookie).
 *   api<Client[]>('/api/clients')
 *   api('/api/clients', { method: 'POST', body: {...} })
 * 401 -> redirect to /login (except on the auth endpoints themselves).
 */
export async function api<T = unknown>(
  path: string,
  opts: { method?: string; body?: Json | FormData; query?: Record<string, string | number | boolean | undefined | null> } = {},
): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  const isForm = opts.body instanceof FormData;
  const res = await fetch(url, {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    credentials: 'same-origin',
    headers: opts.body && !isForm ? { 'content-type': 'application/json' } : undefined,
    body: opts.body ? (isForm ? (opts.body as FormData) : JSON.stringify(opts.body)) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  // a proxy / gateway error page is HTML: keep the status (and the 401 redirect) instead of a SyntaxError (audit F-11)
  let data: unknown;
  try {
    data = text ? (JSON.parse(text) as unknown) : undefined;
  } catch {
    if (res.ok) throw new ApiError(res.status, 'BAD_RESPONSE', 'התקבלה תשובה לא תקינה מהשרת. נסו שוב.');
    data = undefined;
  }
  if (!res.ok) {
    const err = (data as ApiErrorBody | undefined)?.error;
    if (res.status === 401 && !path.startsWith('/api/auth/') && !window.location.pathname.startsWith('/login')) {
      window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
    throw new ApiError(res.status, err?.code ?? 'ERROR', err?.message ?? res.statusText, err?.details);
  }
  return data as T;
}

/** Hebrew message for an error, for toasts. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return 'משהו השתבש. נסו שוב.';
}
